import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Users,
  ArrowRight,
  LogOut,
  MailCheck,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@reflex/ui/components/ui/alert';
import { cn } from '@reflex/ui/lib/utils';
import { PageDescription, PageTitle, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { WizardLayout } from '@reflex/ui/components/layout/WizardLayout';
import { useAuth } from '@/hooks/auth/useAuth';
import { DeclineInviteButton } from '@/components/invite/DeclineInviteButton';
import { WizardOrgSwitcher } from '@/components/setup/WizardOrgSwitcher';
import { api } from '@/lib/api';
import { orgBasePath } from '@/lib/routes';
import { queryKeys } from '@/lib/query-keys';

/**
 * Anti-accidental-org decision screen. Sits between sign-in and the
 * create-new-org form so a user with zero memberships explicitly
 * chooses **join** or **create** before spinning up a new
 * organization. The previous behaviour routed users straight to
 * `/org-setup/new`, which made it trivial to accidentally fork a
 * duplicate org instead of joining a teammate's.
 *
 * "Join" is not a form. Invites are the only join path, they are
 * matched to the signed-in email server-side, and an invite link
 * mailed to that address lands on `/invites/:token` on its own. Asking
 * a user to hunt down and paste that link was the single most
 * confusing thing on this screen, so the join section now just tells
 * them what to ask for and gives them a way to re-check.
 */
export function OrgSetupWelcome() {
  const { user, logout, refreshSession, authenticated, canCreateOrganization, memberships } =
    useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Every place this screen names the account uses the same fallback, and a
  // signed-in user with no email is possible (some providers do not release
  // one), so resolve the label once instead of at each call site.
  const accountLabel = user?.email || 'your account';

  // The gate only routes users here with zero memberships, but the URL is
  // reachable directly — and telling a member of three orgs that they
  // aren't in one is both wrong and the thing that makes this a dead end.
  const hasMemberships = memberships.length > 0;

  // Look up pending invites for the signed-in email so a user who logged
  // in with the invited address is prompted to join directly — without
  // having to paste the share link.
  const pendingInvitesQuery = useQuery({
    queryKey: queryKeys.pendingInvites,
    queryFn: () => api.listPendingInvites(),
    enabled: authenticated,
  });
  const pendingInvites = pendingInvitesQuery.data ?? [];

  async function handleAcceptPending() {
    if (accepting) return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const response = await api.acceptPendingInvites();
      const target = response.joined[0];
      // Repopulate the auth provider's membership state BEFORE navigating
      // (it's backed by useAuth's reducer, not React Query) so OrgGate sees
      // the new membership instead of bouncing back here. Seed the joined
      // org as active since the URL can't yet resolve it from a slug.
      await refreshSession({ preferredActiveOrgId: target?.organizationId ?? null });
      if (target) {
        navigate(orgBasePath(target.organizationSlug), { replace: true });
        return;
      }
      // No org came back (e.g. the invite was revoked between load and
      // accept) — refetch so the stale prompt clears.
      await pendingInvitesQuery.refetch();
      setAcceptError('That invite is no longer available. Try refreshing the page.');
      setAccepting(false);
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'Failed to accept invite');
      setAccepting(false);
    }
  }

  function handleInviteDeclined() {
    // The declined invite drops out of the pending list server-side;
    // refetch so its row disappears (and the whole section hides once
    // the last invite is declined).
    void queryClient.invalidateQueries({ queryKey: queryKeys.pendingInvites });
  }

  return (
    <WizardLayout
      eyebrow="Get started"
      headerSlot={
        <div className="flex items-center gap-2">
          {/* This screen is reachable by URL for a user who *does* have
              memberships (a bookmark, or backing out of "create new org"),
              and its only other exits are creating an org or signing out.
              The switcher hides itself when there is nowhere else to go. */}
          <WizardOrgSwitcher placeholderLabel="Your organizations" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void logout()}
            aria-label="Sign out"
            data-testid="org-setup-welcome-sign-out"
          >
            <LogOut className="size-4" />
            Sign out
          </Button>
        </div>
      }
    >
      <div className="mx-auto max-w-2xl">
        <PageTitle>
          {hasMemberships ? 'Join or create an organization' : "You're not in an organization yet"}
        </PageTitle>
        <PageDescription>
          You're signed in as{' '}
          <span className="font-medium text-foreground" data-testid="org-setup-welcome-account">
            {accountLabel}
          </span>
          {hasMemberships
            ? '. Use the switcher above to go back to an organization you already belong to, or set up another one here. '
            : ", but this account isn't a member of any organization, so there's nothing to show yet. "}
          {canCreateOrganization
            ? 'Get invited to the one your team already uses, or create a new one.'
            : 'Someone in your team has to invite you.'}
        </PageDescription>

        <div className="mt-8 space-y-4">
          {pendingInvites.length > 0 && (
            <section
              className="rounded-lg border border-primary/40 bg-primary/5 p-5"
              data-testid="org-setup-welcome-pending-invites"
            >
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <MailCheck className="size-5" />
                </span>
                <div className="flex-1 space-y-2">
                  <SectionLabel
                    as="h2"
                    className="text-base font-semibold normal-case tracking-normal text-foreground"
                  >
                    {pendingInvites.length === 1
                      ? "You've been invited to join an organization"
                      : "You've been invited to join these organizations"}
                  </SectionLabel>
                  <p className="text-sm text-muted-foreground">
                    We found {pendingInvites.length === 1 ? 'an invite' : 'invites'} for{' '}
                    <span className="font-medium text-foreground">{accountLabel}</span>. Accept to
                    join
                    {pendingInvites.length === 1
                      ? ` ${pendingInvites[0]?.organizationName ?? 'the organization'}`
                      : ''}
                    .
                  </p>
                  <ul className="space-y-1.5 pt-1">
                    {pendingInvites.map((invite) => (
                      <li
                        key={invite.inviteId}
                        className="flex items-center gap-2 text-sm text-foreground"
                        data-testid={`org-setup-welcome-pending-invite-${invite.organizationId}`}
                      >
                        {invite.organizationAvatarUrl ? (
                          <img
                            src={invite.organizationAvatarUrl}
                            alt=""
                            className="size-6 rounded object-cover"
                          />
                        ) : (
                          <span className="flex size-6 items-center justify-center rounded bg-muted text-2xs font-medium text-muted-foreground">
                            {invite.organizationName.slice(0, 2).toUpperCase()}
                          </span>
                        )}
                        <span className="font-medium">{invite.organizationName}</span>
                        <DeclineInviteButton
                          inviteId={invite.inviteId}
                          organizationName={invite.organizationName}
                          onDeclined={handleInviteDeclined}
                          triggerLabel="Decline"
                          className="ml-auto text-muted-foreground"
                          testId={`org-setup-welcome-decline-${invite.organizationId}`}
                        />
                      </li>
                    ))}
                  </ul>
                  <div className="pt-2">
                    <Button
                      type="button"
                      onClick={() => void handleAcceptPending()}
                      disabled={accepting}
                      data-testid="org-setup-welcome-accept-pending"
                    >
                      {accepting
                        ? 'Joining…'
                        : pendingInvites.length === 1
                          ? 'Accept invite'
                          : 'Accept all invites'}
                      <ArrowRight className="size-4" />
                    </Button>
                  </div>
                  {acceptError && (
                    <Alert variant="destructive">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Could not accept invite</AlertTitle>
                      <AlertDescription data-testid="org-setup-welcome-accept-error">
                        {acceptError}
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Only worth showing when there is no invite waiting: telling a user
              to go ask for an invite while one sits accepted-ready above it is
              the contradiction that sent people looking for a link to paste.
              Withheld during the first load too, otherwise an invited user
              reads "go ask to be invited" for as long as the fetch takes. */}
          {!pendingInvitesQuery.isLoading && pendingInvites.length === 0 && (
            <section
              className="rounded-lg border border-border bg-card p-5"
              data-testid="org-setup-welcome-join"
            >
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <Users className="size-5" />
                </span>
                <div className="flex-1 space-y-2">
                  <SectionLabel
                    as="h2"
                    className="text-base font-semibold normal-case tracking-normal text-foreground"
                  >
                    Trying to join your team's organization?
                  </SectionLabel>
                  <p className="text-sm text-muted-foreground">
                    There's no self-serve join. Ask an admin on that team to invite{' '}
                    <span className="font-medium text-foreground">{accountLabel}</span>. Their
                    invite shows up here, and the email they get sent takes you straight in.
                  </p>
                  <div className="pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void pendingInvitesQuery.refetch()}
                      disabled={pendingInvitesQuery.isFetching}
                      data-testid="org-setup-welcome-check-invites"
                    >
                      <RefreshCw
                        className={cn('size-4', pendingInvitesQuery.isFetching && 'animate-spin')}
                      />
                      {pendingInvitesQuery.isFetching ? 'Checking…' : 'Check for invites'}
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section
            className="rounded-lg border border-border bg-card p-5"
            data-testid="org-setup-welcome-create"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                <Building2 className="size-5" />
              </span>
              <div className="flex-1 space-y-2">
                <SectionLabel
                  as="h2"
                  className="text-base font-semibold normal-case tracking-normal text-foreground"
                >
                  Create a new organization
                </SectionLabel>
                {canCreateOrganization ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Start fresh. Best when you're not joining an existing team. You'll be the
                      owner and can invite others later.
                    </p>
                    <div className="pt-2">
                      <Button asChild variant="outline" data-testid="org-setup-welcome-create-cta">
                        <Link to="/org-setup/new">
                          Create new organization
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>
                    </div>
                  </>
                ) : (
                  // Self-serve creation is off platform-wide. Keep the section
                  // visible but replace the dead-end CTA with the reason, so a
                  // user who came here to create an org knows why they can't
                  // and what to do instead.
                  <p
                    className="text-sm text-muted-foreground"
                    data-testid="org-setup-welcome-create-disabled"
                  >
                    An administrator has turned off self-serve organization creation. Ask an
                    administrator to create one for you, or to send you an invite to an existing
                    organization.
                  </p>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </WizardLayout>
  );
}
